"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, Volume2, Droplets, RotateCcw, Waves, ZapOff, PhoneCall, ShieldAlert, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const warningItems = [{
  id: "sound",
  icon: Volume2,
  badge: "Immediate Risk",
  badgeVariant: "destructive" as const,
  title: "Harsh Grinding or Metal Clanking",
  description: "Screeching, rattling, or metallic banging sounds usually indicate jammed impellers, loose mounting rings, or shattered internal blades.",
  risk: "Can seize motor and cause electrical short circuits."
}, {
  id: "leak",
  icon: Droplets,
  badge: "Property Damage",
  badgeVariant: "destructive" as const,
  title: "Puddles Beneath the Sink Base",
  description: "Water pooling under the sink points to cracked housing, failed sink flange seals, or degraded rubber discharge gaskets.",
  risk: "Leads to mold growth, rotted subfloors, and cabinet decay."
}, {
  id: "reset",
  icon: RotateCcw,
  badge: "Electrical Strain",
  badgeVariant: "secondary" as const,
  title: "Constant Reset Button Tripping",
  description: "If the red thermal overload button on the bottom of the unit trips after every single use, the internal motor is drawing excessive current.",
  risk: "Imminent motor burnout requiring complete unit replacement."
}, {
  id: "drain",
  icon: Waves,
  badge: "Plumbing Blockage",
  badgeVariant: "secondary" as const,
  title: "Standing Sink Water & Slow Drainage",
  description: "Water backing up into the sink bowl when running the unit indicates a deep trap obstruction or blunt shredder rings unable to process food.",
  risk: "Can cause overflow contamination across the kitchen counters."
}, {
  id: "hum",
  icon: ZapOff,
  badge: "Motor Seizure",
  badgeVariant: "destructive" as const,
  title: "Humming Sound Without Blade Spin",
  description: "The motor receives electricity and hums quietly, but the flywheel refuses to spin. This is a severe mechanical jam.",
  risk: "Leaving it on can burn out the motor coils within seconds."
}, {
  id: "odor",
  icon: ShieldAlert,
  badge: "Bacterial Hazard",
  badgeVariant: "secondary" as const,
  title: "Foul Odors That Resist Cleaning",
  description: "Rotten, stagnant smells that persist after citrus and baking soda cleaning indicate decaying food trapped inside unreachable motor seals.",
  risk: "Contaminates the drain line and invites pipe corrosion."
}];
export default function WarningSigns() {
  const prefersReduced = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReduced ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: prefersReduced ? 1 : 0,
      y: prefersReduced ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="disposalwarningsigns" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-semibold text-primary border-primary/20">
            <AlertTriangle className="size-3.5 mr-1 text-accent" />
            Kitchen Plumbing Diagnostic
          </Badge>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Warning Signs Your Garbage Disposal Needs Service
          </h2>
          
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Ignoring minor disposal troubles can result in flooded cabinetry, burnt-out motors, or clogged main drain lines. Recognize these urgent signs before total failure occurs in your Jonesboro home.
          </p>
        </div>

        {/* Urgent Warning Alert */}
        <div className="mb-10 max-w-4xl mx-auto">
          <Alert variant="destructive" className="border-destructive/30 bg-destructive/5 text-destructive">
            <AlertTriangle className="size-5" />
            <AlertTitle className="font-semibold text-base">
              Safety First: Never Put Hands Inside a Malfunctioning Unit
            </AlertTitle>
            <AlertDescription className="text-foreground/90 mt-1">
              Always disconnect the power supply at the wall or circuit breaker before inspecting your sink. When in doubt, let a licensed professional clear jams safely with dedicated plumbing equipment.
            </AlertDescription>
          </Alert>
        </div>

        {/* Warning Cards Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }}>
          {warningItems.map((item, i) => {
          const IconComp = item.icon;
          return <motion.div key={item.id} variants={itemVariants} data-index={i} className="flex">
                <Card className="flex flex-col justify-between w-full border border-border hover:border-primary/40 hover:shadow-md transition-all duration-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="size-11 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                        <IconComp className="size-5" />
                      </div>
                      <Badge variant={item.badgeVariant} className="text-xs">
                        {item.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg md:text-xl font-bold font-default text-card-foreground">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm pt-2 leading-relaxed">
                      {item.description}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="pt-2">
                    <Separator className="mb-3" />
                    <div className="flex items-start gap-2 text-xs font-medium text-foreground/80 bg-muted/60 p-2.5 rounded-md">
                      <CheckCircle2 className="size-3.5 text-accent shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-foreground">Risk:</strong> {item.risk}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Call to Action Banner */}
        <div className="bg-muted rounded-2xl p-6 md:p-10 border border-border max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm">
          <div className="space-y-2 text-center md:text-left">
            <h3 className="text-xl md:text-2xl font-bold font-default text-foreground">
              Experiencing any of these warning signs today?
            </h3>
            <p className="text-sm md:text-base text-muted-foreground max-w-xl">
              Jonesboro Emergency Plumber provides prompt repairs, safe unjamming, and reliable disposal replacements 24 hours a day.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
            <Button size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90" asChild>
              <Link to="/contact/">
                <PhoneCall className="size-4 mr-2" />
                Schedule Repair
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="w-full sm:w-auto border-border" asChild>
              <Link to="/problems/">
                View All Problems
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}